import React, { useState, useEffect } from 'react';
import { Input, Select, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import './PhoneInput.css';

const COUNTRY_CODES = [
  { code: '+420', country: 'CZ', flag: '🇨🇿', name: 'Česko' },
  { code: '+421', country: 'SK', flag: '🇸🇰', name: 'Slovensko' },
  { code: '+43', country: 'AT', flag: '🇦🇹', name: 'Rakousko' },
  { code: '+49', country: 'DE', flag: '🇩🇪', name: 'Německo' },
  { code: '+48', country: 'PL', flag: '🇵🇱', name: 'Polsko' },
  { code: '+36', country: 'HU', flag: '🇭🇺', name: 'Maďarsko' },
  { code: '+44', country: 'GB', flag: '🇬🇧', name: 'Velká Británie' },
  { code: '+33', country: 'FR', flag: '🇫🇷', name: 'Francie' },
  { code: '+1', country: 'US', flag: '🇺🇸', name: 'USA/Kanada' },
  { code: '+380', country: 'UA', flag: '🇺🇦', name: 'Ukrajina' },
  { code: '+39', country: 'IT', flag: '🇮🇹', name: 'Itálie' },
  { code: '+34', country: 'ES', flag: '🇪🇸', name: 'Španělsko' },
  { code: '+31', country: 'NL', flag: '🇳🇱', name: 'Nizozemsko' },
  { code: '+41', country: 'CH', flag: '🇨🇭', name: 'Švýcarsko' },
];

/**
 * PhoneInput Component
 * 
 * Features:
 * - Country code selector with flags (required field)
 * - Accepts any format during input (digits, spaces, dashes, parentheses, dots, slashes, commas)
 * - Auto-formats on blur for better readability
 * - Validates that phone number doesn't start with country code (shows error message)
 * - Enforces ITU-T E.164 standard: max 15 digits total (country code + number)
 * - Max 12 digits for phone number field (since country codes are 1-3 digits)
 * - Sends normalized value to backend (+code + digits, preserving commas)
 * - Shows warning if multiple phones detected (comma separated)
 */
const PhoneInput = ({ 
  value, 
  onChange, 
  placeholder = "123 456 789",
  disabled = false,
  readOnly = false,
  style = {},
  ...props 
}) => {
  const { t } = useTranslation();
  const [countryCode, setCountryCode] = useState('+420');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [hasMultiplePhones, setHasMultiplePhones] = useState(false);
  const [validationError, setValidationError] = useState(null);

  // ITU-T E.164 standard: max 15 digits total
  const MAX_TOTAL_DIGITS = 15;
  const MAX_NUMBER_DIGITS = 12; // Since country codes are 1-3 digits

  /**
   * Parse incoming value to extract country code and phone number
   */
  const parsePhoneValue = (phone) => {
    if (!phone) return { code: '+420', number: '' };

    // Handle multiple phones - take first one
    const firstPhone = phone.includes(',') ? phone.split(',')[0].trim() : phone;
    
    // Remove all except digits and +
    const normalized = firstPhone.replace(/[^0-9+]/g, '');
    
    // Find matching country code
    const matchedCountry = COUNTRY_CODES.find(c => normalized.startsWith(c.code));
    
    if (matchedCountry) {
      return {
        code: matchedCountry.code,
        number: normalized.substring(matchedCountry.code.length)
      };
    }
    
    // Default to +420 if no match
    return {
      code: '+420',
      number: normalized.startsWith('+') ? normalized.substring(1) : normalized
    };
  };

  /**
   * Normalize phone number: remove all except digits
   */
  const normalizePhoneNumber = (phone) => {
    if (!phone) return '';
    return phone.replace(/[^0-9]/g, '');
  };

  /**
   * Format phone number for display (add spaces every 3 digits)
   */
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';

    const digits = normalizePhoneNumber(phone);
    if (digits.length <= 3) return digits;

    // Format with spaces every 3 digits from the end
    const chunks = [];
    let remaining = digits;
    
    while (remaining.length > 0) {
      if (remaining.length <= 3) {
        chunks.unshift(remaining);
        break;
      }
      chunks.unshift(remaining.slice(-3));
      remaining = remaining.slice(0, -3);
    }

    return chunks.join(' ');
  };

  /**
   * Handle country code change
   */
  const handleCountryCodeChange = (newCode) => {
    setCountryCode(newCode);

    checkForCountryCodePrefix(phoneNumber, newCode);
    
    // Send updated value to parent
    const normalized = normalizePhoneNumber(phoneNumber);
    if (onChange) {
      onChange(normalized ? newCode + normalized : '');
    }
  };

  /**
   * Check if phone number starts with the selected country code
   * Also validates ITU-T E.164 length (max 15 digits total)
   */
  const checkForCountryCodePrefix = (phone, selectedCode) => {
    if (!phone) {
      setValidationError(null);
      return;
    }
    
    const normalized = normalizePhoneNumber(phone);
    
    // Check if the phone starts with the selected country code (without +)
    const codeWithoutPlus = selectedCode.replace('+', '');
    if (normalized.startsWith(codeWithoutPlus)) {
      setValidationError(t('common.phoneInput.countryCodeInNumber', { code: selectedCode }));
      return;
    }

    // Validate ITU-T E.164: max 15 digits total (country code + number)
    const totalDigits = codeWithoutPlus.length + normalized.length;
    if (totalDigits > MAX_TOTAL_DIGITS) {
      setValidationError(t('common.phoneInput.tooLong', { max: MAX_TOTAL_DIGITS }));
      return;
    }

    setValidationError(null);
  };

  /**
   * Handle phone number input change
   */
  const handlePhoneNumberChange = (e) => {
    const inputValue = e.target.value;
    // Allowed only: numbers, space, -, (, ), ., /, ,
    const filtered = inputValue.replace(/[^0-9\s\-().,/]/g, '');
    
    // Enforce max length: 12 digits for the number field
    const digitsOnly = normalizePhoneNumber(filtered);
    if (digitsOnly.length > MAX_NUMBER_DIGITS) {
      return; // Don't allow more than 12 digits
    }
    
    setPhoneNumber(filtered);
    
    // Check for multiple phones
    setHasMultiplePhones(filtered.includes(','));
    
    // Send normalized value to parent
    const normalized = normalizePhoneNumber(filtered);
    if (onChange) {
      onChange(normalized ? countryCode + normalized : '');
    }
  };

  /**
   * Handle blur - format for display and validate country code
   */
  const handleBlur = () => {
    checkForCountryCodePrefix(phoneNumber, countryCode);
    
    const formatted = formatPhoneNumber(phoneNumber);
    setPhoneNumber(formatted);
    
    // Update parent with normalized value
    const normalized = normalizePhoneNumber(phoneNumber);
    if (onChange) {
      onChange(normalized ? countryCode + normalized : '');
    }
  };

  /**
   * Handle focus - keep formatted value
   */
  const handleFocus = () => {
    // Keep current display value on focus
  };

  // Sync with external value changes
  useEffect(() => {
    if (value !== undefined && value !== null) {
      const parsed = parsePhoneValue(value);
      setCountryCode(parsed.code);
      setPhoneNumber(formatPhoneNumber(parsed.number));
      setHasMultiplePhones(value?.includes(',') || false);
      setValidationError(null);
    } else {
      setCountryCode('+420');
      setPhoneNumber('');
      setHasMultiplePhones(false);
      setValidationError(null);
    }
  }, [value]);

  // Country code selector (required field)
  const countryCodeSelector = (
    <Select
      value={countryCode}
      onChange={handleCountryCodeChange}
      disabled={disabled || readOnly}
      className="phone-country-select"
      style={{ width: 85, paddingRight: 3 }}
      showSearch
      optionFilterProp="children"
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      options={COUNTRY_CODES.map(c => ({
        value: c.code,
        label: `${c.flag} ${c.code}`,
        title: c.name
      }))}
      status={!countryCode ? 'error' : ''}
    />
  );

  const inputElement = (
    <Input
      addonBefore={countryCodeSelector}
      suffix={
        hasMultiplePhones ? (
          <Tooltip title={t('common.phoneInput.multiplePhonesShort')}>
            <WarningOutlined style={{ color: '#faad14' }} />
          </Tooltip>
        ) : null
      }
      placeholder={(disabled || readOnly) ? '' : placeholder}
      value={phoneNumber}
      onChange={handlePhoneNumberChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      disabled={disabled}
      readOnly={readOnly}
      status={validationError ? 'error' : ''}
      style={style}
      {...props}
    />
  );

  if (validationError) {
    return (
      <Tooltip title={validationError} color="red" open={true}>
        {inputElement}
      </Tooltip>
    );
  }

  if (hasMultiplePhones) {
    return (
      <Tooltip 
        title={t('common.phoneInput.multiplePhones')}
        color="orange"
      >
        {inputElement}
      </Tooltip>
    );
  }

  return inputElement;
};

export default PhoneInput;
